'use client';

import { useState } from 'react';
import { dataLayerPush } from '@libs/analytics';
import { activateOffer, getOffers as fetchMemberOffers } from '@libs/api-proxy-client';
import { AccountDrawerType, logSentryError, SentryAcpTreatsRewardsErrors as SentryErrors, useAlertBanner, useLoyaltyContext, useOptimizely, usePageLayout } from '@libs/common-next';
import type { Offer } from '@libs/common-types';
import { ensureAuth0AccessToken, evaluateSession, isUserLoggedIn } from '@libs/core';
import { SnackbarUtils } from '@libs/shared-ui/utils';
import { applyMemberState } from '../../utils/apply-member-state';
export function getCardSize(offer?: Offer, isFeatured?: boolean) {
  if (isFeatured) {
    return 'featured';
  }
  return offer?.cardSize.toLowerCase() === 'large' ? 'large' : 'small';
}
export function useOfferCardActions() {
  const {
    drawerOpen,
    getOffers,
    isActivating,
    selectedOffer,
    setDrawerOpen,
    setSelectedOffer
  } = useLoyaltyContext();
  const {
    isAuthenticated,
    openAccountDrawer,
    setSignInDrawerProps,
    user
  } = usePageLayout();
  const {
    setAlert
  } = useAlertBanner();
  const {
    context
  } = useOptimizely();
  const [activatingOffers, setActivatingOffers] = useState<Set<string>>(new Set());
  function handleCardClick(offer: Offer) {
    setDrawerOpen(true);
    setSelectedOffer(offer);
    context?.trackEvent('offer_details_modal_click');
    // Reset error so pre-existing error doesn't display inside offer drawer
    setAlert(undefined);
  }
  function handleCloseDrawer() {
    setDrawerOpen(false);
    setSelectedOffer(undefined);

    // Reset error so error from offer drawer doesn't display on offer grid
    setAlert(undefined);
  }
  async function handleActivateOffer(offer_0?: Offer, gtmSection?: string, postLoginAction: 'openDetails' | 'activate' = 'openDetails') {
    if (!offer_0?.isActivatable || offer_0.isActivated) {
      return;
    }
    if (!isAuthenticated && !user) {
      setSignInDrawerProps({
        onSuccess: () => handleOfferSignInSuccess(offer_0, postLoginAction, gtmSection)
      });
      openAccountDrawer(AccountDrawerType.SIGN_IN);
      return;
    }
    await activateAuthenticatedOffer(offer_0, gtmSection);
  }
  async function handleOfferSignInSuccess(offer_1: Offer, postLoginAction_0: 'openDetails' | 'activate', gtmSection_0?: string) {
    try {
      const response = await fetchMemberOffers();
      if (!response) {
        throw new Error('Failed to fetch offers after sign-in');
      }
      if (!isUserLoggedIn()) return;
      const memberOffer = response.offers.find(candidate => candidate.offerId === offer_1.offerId);
      if (!memberOffer) return;
      const offerWithMemberState = applyMemberState(offer_1, memberOffer);
      setSelectedOffer(offerWithMemberState);
      setDrawerOpen(true);
      if (postLoginAction_0 === 'activate') {
        await activateAuthenticatedOffer(offerWithMemberState, gtmSection_0);
      }
    } catch (error) {
      SnackbarUtils.showPredefined('SERVER_ERROR');
      logSentryError(error, SentryErrors.OfferCard.ActivateOfferFailed);
    }
  }
  async function activateAuthenticatedOffer(offer_2: Offer, gtmSection_1?: string) {
    if (!offer_2.isActivatable || offer_2.isActivated) return;
    setActivatingOffers(prevOffers => new Set(prevOffers).add(offer_2.offerId));
    try {
      const sessionStillValid = await evaluateSession({});
      if (!sessionStillValid) {
        return;
      }
      await ensureAuth0AccessToken();
      if (!isUserLoggedIn()) {
        return;
      }
      const response_0 = await activateOffer(offer_2);
      if (response_0 && 'success' in response_0 && response_0?.success) {
        setAlert(undefined);
        context?.trackEvent('offer_activations');
        // The click closure may still hold the pre-login offer after sign-in.
        setSelectedOffer(currentOffer => currentOffer?.offerId === offer_2.offerId ? {
          ...currentOffer,
          isActivated: true
        } : currentOffer);
        // Fetch offers again from API to get updated data after activation
        await getOffers();
      } else {
        throw new Error(`Failed to activate an offer, offerId: ${offer_2.offerId}`);
      }
    } catch (error_0) {
      SnackbarUtils.showPredefined('SERVER_ERROR');
      dataLayerPush({
        event: 'error',
        site_section: gtmSection_1 || 'treats rewards',
        error_message: `uh oh! [${offer_2.shortDescription}] order didn't activate. please try again.`
      });
      logSentryError(error_0, SentryErrors.OfferCard.ActivateOfferFailed);
    } finally {
      setActivatingOffers(prevOffers => {
        const updatedSet = new Set(prevOffers);
        updatedSet.delete(offer_2.offerId);
        return updatedSet;
      });
    }
  }
  function showSpinner(offer_3?: Offer) {
    return offer_3 ? activatingOffers.has(offer_3.offerId) : false;
  }
  return {
    drawerOpen,
    handleActivateOffer,
    handleCardClick,
    handleCloseDrawer,
    isActivating,
    selectedOffer,
    setDrawerOpen,
    setSelectedOffer,
    showSpinner
  };
}